import { useState } from 'react';
import { MoreHoriz, Settings, Sparks, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import type { IFolder } from 'features/chat/agent-chat-module/types/kb.service.type';
import { useDeleteFolder } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { AddFolderToAgentDialog } from './add-folder-to-agent-dialog';
import { ConfigureFolderModal } from './configure-folder-modal';
import { getApiErrorDetail } from 'utils/api-error';

type FolderCardDropdownProps = {
  projectKey: string;
  folder: IFolder;
};

export function FolderCardDropdown({ projectKey, folder }: FolderCardDropdownProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const canDelete = useIsOwner(folder);
  const { mutateAsync: deleteFolder, isPending } = useDeleteFolder();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [isAddToAgentOpen, setIsAddToAgentOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteFolder({
        folder_id: folder._id,
        project_key: projectKey,
      });

      toast({
        variant: 'success',
        title: 'Folder deleted',
        description: `${folder.Name || 'Untitled folder'} was deleted successfully.`,
      });
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast({
        title: 'Failed to delete folder',
        description: getApiErrorDetail(error) ?? 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded text-grey-700 opacity-100 hover:bg-grey-50 hover:text-grey-700 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-white/70 dark:hover:bg-white/10"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <MoreHoriz width={20} height={20} strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" sideOffset={6} className="min-w-[191px]">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsMenuOpen(false);
              setIsConfigureModalOpen(true);
            }}
          >
            <Settings width={20} height={20} strokeWidth={1.5} />
            {t('CONFIGURE') || 'Configure'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsMenuOpen(false);
              setIsAddToAgentOpen(true);
            }}
          >
            <Sparks width={20} height={20} strokeWidth={1.5} />
            {t('ADD_TO_AGENT') || 'Add to agent'}
          </DropdownMenuItem>
          {canDelete ? (
            <DropdownMenuItem
              className={dropdownMenuItemDestructiveClass}
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
            >
              <Trash width={20} height={20} strokeWidth={1.5} />
              {t('DELETE') || 'Delete'}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfigureFolderModal
        projectKey={projectKey}
        folder={folder}
        open={isConfigureModalOpen}
        onOpenChange={setIsConfigureModalOpen}
      />

      <AddFolderToAgentDialog
        open={isAddToAgentOpen}
        onOpenChange={setIsAddToAgentOpen}
        projectKey={projectKey}
        folderId={folder._id}
      />

      <ConfirmationModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title={t('DELETE_FOLDER') || 'Delete folder'}
        description={
          t('DELETE_FOLDER_CONFIRMATION_DESCRIPTION', {
            name: folder.Name || t('THIS_FOLDER') || 'this folder',
          }) ||
          `Are you sure you want to delete ${folder.Name || 'this folder'}? This action cannot be undone.`
        }
        onConfirm={handleDelete}
        confirmText={t('DELETE') || 'Delete'}
        cancelText={t('CANCEL') || 'Cancel'}
        preventAutoClose
        isLoading={isPending}
        confirmVariant="delete"
      />
    </>
  );
}
